import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as _ from 'lodash-es';
import { AnimatePresence } from 'motion/react';
import type { Delta, Op } from 'quill/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import RightPanel, { useRightPanelPinnedState } from '../app/layout/RightPanel';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import CaretButton from '../components/CaretButton';
import EmptyState from '../components/EmptyState';
import Menu, { MenuItem, MenuSeparator } from '../components/Menu';
import OpacityMotionTransition from '../components/OpacityMotionTransition';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import useMarkdownToHTML from '../components/useMarkdownToHTML';
import QuillContext, { useQuill } from '../editor/QuillContext';
import CopilotMessageComposer, {
  getMessageTextFromOps,
} from '../editor/copilot/CopilotMessageComposer';
import deltaToText from '../editor/utils/deltaToText';
import findClosestScrollingContainer from '../editor/utils/findClosestScrollingContainer';
import arrowDownIcon from '../icons/non-prefilled/16x16/arrowDown.svg';
import commentAddIcon from '../icons/non-prefilled/16x16/commentAdd.svg';
import pinOutlineIcon from '../icons/non-prefilled/16x16/pinOutline.svg';
import BetaBadge from '../organization/BetaBadge';
import HoverContentPreview from '../post/HoverContentPreview';
import { LinkStateContext } from '../utils/router/Link';
import useSlug from '../utils/router/useSlug';
import * as styles from './AiCopilotChatPanel.module.scss';
import Messages, {
  MESSAGES_LIST_PADDING_BOTTOM,
  getMessageElementById,
} from './Messages';
import type { InputSources, Source } from './useAiCopilot';
import useAiCopilot from './useAiCopilot';

type BaseSystemMessage = {
  id: string;
  type: 'system';
};

export type SystemMessage = BaseSystemMessage &
  (
    | {
        status: 'finished';
        responseId: string;
        text: string;
        sources: Source[];
      }
    | {
        status: 'error';
        error: string;
      }
  );

export type UserMessage = {
  id: string;
  type: 'user';
  content: Op[];
};

export type Message = UserMessage | SystemMessage;

type Conversation = {
  id: string;
  createdAt: string;
  name?: string;
  messages: Message[];
};

type AllConversationsMap = Record<string, Conversation>;

type PendingSystemMessage = BaseSystemMessage & {
  status: 'pending';
};

type StartOrSearchSystemMessage = BaseSystemMessage & {
  status: 'start' | 'search' | 'reason';
  responseId: string;
};

type UpdateSystemMessage = BaseSystemMessage & {
  status: 'update';
  responseId: string;
  text: string;
};

export type GeneratingSystemMessage =
  | PendingSystemMessage
  | StartOrSearchSystemMessage
  | UpdateSystemMessage;

const AI_COPILOT_CONVERSATIONS_KEY = 'ai-copilot-conversations';

const conversationsAtom = atomWithStorage<Record<string, AllConversationsMap>>(
  AI_COPILOT_CONVERSATIONS_KEY,
  {},
);

const useAllConversations = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [conversations, setConversations] = useAtom(conversationsAtom);

  const setConversation = (
    conversationId: string,
    callback: (conversation: Conversation | null) => Conversation | null,
  ) => {
    setConversations((prev) => {
      const conversationMap = prev[currentUser.id] ?? {};
      const conversation = callback(conversationMap[conversationId] ?? null);
      if (!conversation) {
        return {
          ...prev,
          [currentUser.id]: _.omit(conversationMap, conversationId),
        };
      }
      return {
        ...prev,
        [currentUser.id]: {
          ...conversationMap,
          [conversation.id]: conversation,
        },
      };
    });
  };

  const allConversationsMap = useMemo(
    () => conversations[currentUser.id] ?? {},
    [conversations, currentUser.id],
  );

  const clearAllConversations = () => setConversations({});

  return {
    allConversationsMap,
    setConversation,
    clearAllConversations,
  };
};

interface ScrollToBottomButtonProps {
  isScrolledToBottom: boolean;
  onClick: () => void;
}

const ScrollToBottomButton = ({
  isScrolledToBottom,
  onClick,
}: ScrollToBottomButtonProps) => {
  return (
    <AnimatePresence>
      {!isScrolledToBottom && (
        <OpacityMotionTransition
          className={styles.scrollToBottomButtonContainer}
        >
          <Button
            aria-label="Scroll to bottom"
            onClick={onClick}
            className={styles.scrollToBottomButton}
          >
            <SVGIcon size={16}>{arrowDownIcon}</SVGIcon>
          </Button>
        </OpacityMotionTransition>
      )}
    </AnimatePresence>
  );
};

const useIsScrolledToBottom = (
  containerRef: React.RefObject<HTMLElement | null>,
) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  useEffect(() => {
    const messagesContainer = containerRef.current;
    if (!messagesContainer) return undefined;

    const scrollContainer = findClosestScrollingContainer(containerRef.current);
    if (!scrollContainer) return undefined;

    const handleScrollOrResize = () => {
      setIsScrolledToBottom(
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - MESSAGES_LIST_PADDING_BOTTOM,
      );
    };

    const observer = new ResizeObserver(handleScrollOrResize);
    observer.observe(messagesContainer);

    scrollContainer.addEventListener('scroll', handleScrollOrResize);

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [containerRef]);

  return isScrolledToBottom;
};

const scrollToMessage = (messageId: string, messagesContainer: HTMLElement) => {
  const messageElement = getMessageElementById(messageId);
  if (!messageElement) return;

  const scrollContainer = findClosestScrollingContainer(messageElement);
  if (!scrollContainer) return;

  const messageRect = messageElement.getBoundingClientRect();
  const messagesContainerRect = messagesContainer.getBoundingClientRect();
  const scrollTop = messageRect.top - messagesContainerRect.top - 16;

  scrollContainer.scrollTo({
    top: scrollTop,
    behavior: 'smooth',
  });
};

const MAX_CONVERSATIONS_TO_SHOW = 10;
const DEFAULT_CONVERSATION_NAME = 'New chat';

const getConversationName = (conversation: Conversation) => {
  if (conversation.name) {
    return conversation.name;
  }

  const firstMessage = conversation.messages[0];

  return firstMessage?.type === 'user'
    ? deltaToText(firstMessage.content) || DEFAULT_CONVERSATION_NAME
    : DEFAULT_CONVERSATION_NAME;
};

interface HistoricalConversationsMenuProps {
  allConversationsMap: AllConversationsMap;
  onConversationClick: (conversationId: string) => void;
  onClearAllHistoryClick: () => void;
  disabled: boolean;
  children: (isOpen: boolean) => React.ReactNode;
}

const HistoricalConversationsMenu = ({
  allConversationsMap,
  onConversationClick,
  onClearAllHistoryClick,
  disabled,
  children,
}: HistoricalConversationsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const sortedConversations = Object.values(allConversationsMap).toSorted(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const recentConversations = sortedConversations.slice(
    0,
    MAX_CONVERSATIONS_TO_SHOW,
  );

  const handleConversationClick = (conversationId: string) => {
    onConversationClick(conversationId);
    setIsOpen(false);
  };

  if (disabled) {
    return children(false);
  }

  return (
    <PopOverPrimitive
      trigger="click"
      placement="bottom-start"
      offset={6}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopOverPrimitive.Trigger>{children(isOpen)}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content>
          <Menu>
            {recentConversations.map((historicalConversation) => {
              return (
                <MenuItem
                  className={styles.menuItem}
                  key={historicalConversation.id}
                  onClick={() =>
                    handleConversationClick(historicalConversation.id)
                  }
                >
                  {getConversationName(historicalConversation)}
                </MenuItem>
              );
            })}
            <MenuSeparator />
            <MenuItem
              danger
              disabled={sortedConversations.length === 0}
              onClick={() => {
                onClearAllHistoryClick();
                setIsOpen(false);
              }}
            >
              Clear chat history
            </MenuItem>
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

type InnerAiCopilotChatPanelProps = {
  conversationId: string;
  messages: (Message | GeneratingSystemMessage)[];
  allConversationsMap: AllConversationsMap;
  sendMessage: (delta: Delta) => UserMessage | null;
  startNewConversation: () => void;
  openConversationById: (conversationId: string) => void;
  clearAllHistory: () => void;
  stopGenerating: () => void;
  retryAnswer: (message: UserMessage) => void;
  retryAnswerInNewChat: (message: UserMessage) => void;
  inputSources: InputSources;
  setInputSources: React.Dispatch<React.SetStateAction<InputSources>>;
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const InnerAiCopilotChatPanel = ({
  conversationId,
  messages,
  allConversationsMap,
  sendMessage,
  startNewConversation,
  openConversationById,
  clearAllHistory,
  stopGenerating,
  retryAnswer,
  retryAnswerInNewChat,
  inputSources,
  setInputSources,
}: InnerAiCopilotChatPanelProps) => {
  const { isRightPanelPinned, isRightPanelPinnable, setIsRightPanelPinned } =
    useRightPanelPinnedState();
  const [messageToEdit, setMessageToEdit] = useState<UserMessage | null>(null);
  const lastMessage = messages.at(-1);
  const isGenerating =
    lastMessage?.type === 'system' &&
    lastMessage.status !== 'finished' &&
    lastMessage.status !== 'error';

  const markdownToHTML = useMarkdownToHTML();
  const { quill } = useQuill();

  const resetMessageToEdit = () => {
    setMessageToEdit((current) => {
      if (current && quill) {
        quill.setContents([]);
        quill.focus();
      }
      return null;
    });
  };

  const resetMessageToEditEvent = useEffectEvent(resetMessageToEdit);

  useEffect(() => {
    resetMessageToEditEvent();
  }, [conversationId]);

  const footerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const handleSend = () => {
    if (!quill) return;

    const userMessage = sendMessage(quill.getContents());
    if (userMessage) {
      quill.setContents([]);
      quill.focus();

      setTimeout(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;
        scrollToMessage(userMessage.id, messagesContainer);
      });
    }
  };

  const handleScrollToBottom = (behavior?: ScrollBehavior) => {
    const scrollContainer = findClosestScrollingContainer(
      messagesContainerRef.current,
    );
    if (!scrollContainer) return;
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior,
    });
  };

  const handleScrollToBottomEvent = useEffectEvent(handleScrollToBottom);

  useEffect(() => {
    handleScrollToBottomEvent();
  }, [conversationId]);

  const isScrolledToBottom = useIsScrolledToBottom(messagesContainerRef);

  const isHistoryButtonDisabled =
    messages.length === 0 && Object.keys(allConversationsMap).length === 0;

  return (
    <RightPanel.Inner
      header={
        <RightPanel.NewHeader
          title={
            <>
              <HistoricalConversationsMenu
                allConversationsMap={allConversationsMap}
                onConversationClick={(id) => {
                  if (id === 'new') {
                    startNewConversation();
                  } else {
                    openConversationById(id);
                  }
                  quill?.focus();
                }}
                onClearAllHistoryClick={clearAllHistory}
                disabled={isHistoryButtonDisabled}
              >
                {(isOpen) => (
                  <CaretButton
                    disabled={isHistoryButtonDisabled}
                    active={isOpen}
                    aria-label="Chat history"
                  >
                    AI Copilot
                  </CaretButton>
                )}
              </HistoricalConversationsMenu>

              <span className={styles.badge}>
                <BetaBadge />
              </span>
            </>
          }
          iconButtonsExtra={
            <>
              <Tooltip content="New chat">
                <RightPanel.IconButton
                  icon={commentAddIcon}
                  disabled={messages.length === 0}
                  onClick={() => {
                    startNewConversation();
                    quill?.focus();
                  }}
                  aria-label="New chat"
                />
              </Tooltip>
              <Tooltip content={isRightPanelPinned ? 'Unpin chat' : 'Pin chat'}>
                <RightPanel.IconButton
                  icon={pinOutlineIcon}
                  disabled={!isRightPanelPinnable}
                  onClick={() => setIsRightPanelPinned(!isRightPanelPinned)}
                  aria-label={isRightPanelPinned ? 'Unpin chat' : 'Pin chat'}
                  enabled={isRightPanelPinned}
                />
              </Tooltip>
            </>
          }
        />
      }
      footer={
        <div ref={footerRef} className={styles.footer}>
          <ScrollToBottomButton
            isScrolledToBottom={isScrolledToBottom}
            onClick={() => {
              handleScrollToBottom('smooth');
            }}
          />

          <CopilotMessageComposer
            editControl={
              messageToEdit
                ? {
                    onCancel: () => {
                      if (!quill) return;
                      resetMessageToEdit();
                    },
                    onEdit: () => {
                      if (!quill) return;
                      retryAnswer({
                        ...messageToEdit,
                        content: quill.getContents().ops,
                      });
                      if (messagesContainerRef.current) {
                        scrollToMessage(
                          messageToEdit.id,
                          messagesContainerRef.current,
                        );
                      }
                      resetMessageToEdit();
                    },
                  }
                : undefined
            }
            isGenerating={isGenerating}
            onSend={handleSend}
            onStop={stopGenerating}
            inputSources={inputSources}
            setInputSources={setInputSources}
          />
        </div>
      }
    >
      <HoverContentPreview>
        <Messages.Container
          ref={messagesContainerRef}
          hasMessages={messages.length > 0}
          footerRef={footerRef}
        >
          {markdownToHTML && (
            <Messages.List messages={messages}>
              {(message) => {
                if (message.type === 'system') {
                  return (
                    <Messages.SystemMessage
                      message={message}
                      markdownToHTML={markdownToHTML}
                    />
                  );
                }

                return (
                  <Messages.UserMessage
                    message={message}
                    isEditing={messageToEdit?.id === message.id}
                    overflowMenu={
                      <Messages.UserMessageOverflowMenu
                        onRetry={() => retryAnswer(message)}
                        onRetryInNewChat={() => retryAnswerInNewChat(message)}
                        onEdit={() => {
                          if (!quill) return;
                          setMessageToEdit(message);
                          quill.setContents(message.content);
                          quill.setSelection(quill.getLength(), 0);
                          quill.focus();
                        }}
                        isGenerating={isGenerating}
                      />
                    }
                  />
                );
              }}
            </Messages.List>
          )}

          {messages.length === 0 && (
            <EmptyState imageName="aiCopilot" noMargin />
          )}
        </Messages.Container>
      </HoverContentPreview>
    </RightPanel.Inner>
  );
};

const findLastSystemMessage = (messages: Message[]) => {
  return messages
    .toReversed()
    .find((m) => m.type === 'system' && m.status !== 'error');
};

type ConversationData = {
  conversationId: string;
  channelId: string;
  generatingSystemMessage: GeneratingSystemMessage | null;
  initialMessage: Delta | null;
};

const newConversationData = (initialMessage?: Delta): ConversationData => ({
  conversationId: crypto.randomUUID(),
  channelId: crypto.randomUUID(),
  generatingSystemMessage: null,
  initialMessage: initialMessage ?? null,
});

const conversationDataAtom = atom<ConversationData>(newConversationData());

export const useConversationData = () => {
  const [conversationData, setCurrentConversationData] =
    useAtom(conversationDataAtom);

  const startNewConversation = (initialMessage?: Delta) => {
    setCurrentConversationData(newConversationData(initialMessage));
  };

  return {
    conversationData,
    setCurrentConversationData,
    startNewConversation,
  };
};

const AiCopilotChatPanel = () => {
  const [currentPostId] = useSlug('postSlug');
  const [previousPostId, setPreviousPostId] = useState<string | null>(null);
  const [inputSources, setInputSources] = useState<InputSources>({
    slab: {
      enabled: true,
    },
    web: {
      enabled: false,
    },
    mentionedPosts: {
      enabled: false,
      postIds: [],
    },
    currentPost: {
      enabled: false,
    },
  });

  if (previousPostId !== currentPostId) {
    setPreviousPostId(currentPostId);
    setInputSources((prev) => ({
      ...prev,
      currentPost: { enabled: false },
    }));
  }

  const {
    conversationData: {
      conversationId,
      channelId,
      generatingSystemMessage,
      initialMessage,
    },
    setCurrentConversationData,
    startNewConversation,
  } = useConversationData();

  useEffect(() => {
    return () => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));
    };
  }, [setCurrentConversationData]);

  const { allConversationsMap, setConversation, clearAllConversations } =
    useAllConversations();

  const aiCopilot = useAiCopilot({
    channelId,
    onProgress: (responseId, status) => {
      setCurrentConversationData((current) => {
        if (!current.generatingSystemMessage) {
          return {
            ...current,
            generatingSystemMessage: {
              id: crypto.randomUUID(),
              type: 'system',
              status: 'start',
              responseId,
            },
          };
        }

        if (current.generatingSystemMessage.status === 'pending') {
          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              responseId,
              status,
            },
          };
        }

        if (
          (current.generatingSystemMessage.status === 'start' ||
            current.generatingSystemMessage.status === 'reason' ||
            current.generatingSystemMessage.status === 'search') &&
          current.generatingSystemMessage.responseId === responseId
        ) {
          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              status,
            },
          };
        }

        return current;
      });
    },
    onAnswerText: (responseId, text) => {
      setCurrentConversationData((current) => {
        if (
          current.generatingSystemMessage &&
          current.generatingSystemMessage.status !== 'pending' &&
          current.generatingSystemMessage.responseId === responseId
        ) {
          if (current.generatingSystemMessage.status === 'update') {
            return {
              ...current,
              generatingSystemMessage: {
                ...current.generatingSystemMessage,
                text: current.generatingSystemMessage.text + text,
              },
            };
          }

          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              status: 'update',
              text,
            },
          };
        }

        return current;
      });
    },
    onError: (error) => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));

      setConversation(conversationId, (current) => {
        if (!current) return current;
        return {
          ...current,
          messages: [
            ...current.messages,
            {
              id: crypto.randomUUID(),
              status: 'error',
              error,
              type: 'system',
            },
          ],
        };
      });
    },
    onFinish: (responseId, sources, text) => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));

      setConversation(conversationId, (current) => {
        if (!current) return current;
        return {
          ...current,
          messages: [
            ...current.messages,
            {
              id: responseId,
              status: 'finished',
              responseId,
              text,
              sources,
              type: 'system',
            },
          ],
        };
      });
    },
  });

  const retryAnswerInNewChat = (message: UserMessage) => {
    const currentConversation = allConversationsMap[conversationId];
    if (!currentConversation) return;

    const newConversationId = crypto.randomUUID();

    const messageIndex = currentConversation.messages.findIndex(
      (m) => m.id === message.id,
    );
    const messagesToCopy = currentConversation.messages.slice(
      0,
      messageIndex + 1,
    );
    const lastSystemMessage = findLastSystemMessage(messagesToCopy);

    const text = getMessageTextFromOps(message.content);

    if (
      aiCopilot.sendMessage({
        input: text,
        inputSources,
        retry: true,
        previousId: lastSystemMessage?.id ?? null,
      })
    ) {
      setConversation(newConversationId, () => {
        return {
          id: newConversationId,
          createdAt: new Date().toISOString(),
          name: `(Copy) ${getConversationName(currentConversation)}`,
          messages: messagesToCopy,
        };
      });

      setCurrentConversationData((current) => ({
        // we keep the current channelId so that the channel connection is not lost
        channelId: current.channelId,
        conversationId: newConversationId,
        generatingSystemMessage: {
          id: crypto.randomUUID(),
          type: 'system',
          status: 'pending',
        },
        initialMessage: null,
      }));
    }
  };

  const retryAnswer = (message: UserMessage) => {
    const currentConversation = allConversationsMap[conversationId];
    if (!currentConversation) return;

    const messageIndex = currentConversation.messages.findIndex(
      (m) => m.id === message.id,
    );
    const newMessages = currentConversation.messages.slice(0, messageIndex);
    const lastSystemMessage = findLastSystemMessage(newMessages);

    const text = getMessageTextFromOps(message.content);

    if (
      aiCopilot.sendMessage({
        input: text,
        inputSources,
        retry: true,
        previousId: lastSystemMessage?.id ?? null,
      })
    ) {
      setConversation(conversationId, (current) => {
        if (!current) return current;

        return {
          ...current,
          messages: [...newMessages, message],
        };
      });

      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: {
          id: crypto.randomUUID(),
          type: 'system',
          status: 'pending',
        },
      }));
    }
  };

  const sendMessage = (delta: Delta) => {
    const currentConversation = allConversationsMap[conversationId];
    const lastSystemMessage = currentConversation
      ? findLastSystemMessage(currentConversation.messages)
      : null;

    const text = getMessageTextFromOps(delta.ops);

    if (
      aiCopilot.sendMessage({
        input: text,
        inputSources,
        previousId: lastSystemMessage?.id ?? null,
      })
    ) {
      const userMessage: UserMessage = {
        id: crypto.randomUUID(),
        type: 'user',
        content: delta.ops,
      };

      setConversation(conversationId, (current) => {
        if (!current) {
          return {
            id: conversationId,
            createdAt: new Date().toISOString(),
            messages: [userMessage],
          };
        }

        return {
          ...current,
          messages: [...current.messages, userMessage],
        };
      });

      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: {
          id: crypto.randomUUID(),
          type: 'system',
          status: 'pending',
        },
      }));

      return userMessage;
    }

    return null;
  };

  const clearAllHistory = () => {
    clearAllConversations();
    startNewConversation();
  };

  const stopGenerating = () => {
    setCurrentConversationData((current) => ({
      ...current,
      // we regenerate the channelId when stopping, so that we don't
      // have to deal with update messages from the previous prompt
      channelId: crypto.randomUUID(),
      generatingSystemMessage: null,
    }));
  };

  const messages = useMemo(() => {
    const conversationMessages = allConversationsMap[conversationId]?.messages;
    if (!conversationMessages) return [];

    return generatingSystemMessage
      ? [...conversationMessages, generatingSystemMessage]
      : conversationMessages;
  }, [allConversationsMap, conversationId, generatingSystemMessage]);

  const hasMessages = messages.length > 0;

  const sendMessageEvent = useEffectEvent(sendMessage);

  useEffect(() => {
    if (initialMessage && !hasMessages) {
      sendMessageEvent(initialMessage);
    }
  }, [initialMessage, hasMessages]);

  const linkState = useMemo(() => ({ keepRightPanelOpen: true }), []);

  return (
    <QuillContext>
      <LinkStateContext.Provider value={linkState}>
        <InnerAiCopilotChatPanel
          conversationId={conversationId}
          allConversationsMap={allConversationsMap}
          messages={messages}
          sendMessage={sendMessage}
          startNewConversation={startNewConversation}
          clearAllHistory={clearAllHistory}
          openConversationById={(id) => {
            setCurrentConversationData({
              conversationId: id,
              channelId: crypto.randomUUID(),
              generatingSystemMessage: null,
              initialMessage: null,
            });
          }}
          stopGenerating={stopGenerating}
          retryAnswer={retryAnswer}
          retryAnswerInNewChat={retryAnswerInNewChat}
          inputSources={inputSources}
          setInputSources={setInputSources}
        />
      </LinkStateContext.Provider>
    </QuillContext>
  );
};

export default AiCopilotChatPanel;
